import { motion } from "framer-motion";
import { QrCode, UserRoundPlus, MessagesSquare, Flame } from "lucide-react";
import { useLang } from "@/lib/i18n";

const QR_IMG =
  "https://images.unsplash.com/photo-1580929753530-ef52238116c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwd29vZCUyMHJlc3RhdXJhbnQlMjB0YWJsZSUyMHdpbmV8ZW58MHx8fHwxNzkwODA1OTIyfDA&ixlib=rb-4.1.0&q=85";
const SMS_IMG =
  "https://images.unsplash.com/photo-1770584848378-1db64da8b869?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjBsb29raW5nJTIwYXQlMjBwaG9uZSUyMGluJTIwZGltJTIwcmVzdGF1cmFudHxlbnwwfHx8fDE3OTA4MDU5MjJ8MA&ixlib=rb-4.1.0&q=85";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const card =
  "bg-[#1A1512] rounded-xl border border-[#2D2520] overflow-hidden group hover:-translate-y-1 hover:border-[#D48C46]/50 transition-all duration-500";

const ICONS = [QrCode, UserRoundPlus, MessagesSquare];

export default function HowItWorks() {
  const { t } = useLang();
  const [s1, s2, s3] = t.how.steps;

  return (
    <section
      id="slik-fungerer-det"
      className="py-24 md:py-32"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...reveal} transition={{ duration: 0.8 }} className="mb-16 md:mb-20">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D48C46] mb-5">
            {t.how.label}
          </p>
          <h2 className="font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-[#F9F6F0] max-w-2xl">
            {t.how.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <motion.div
            {...reveal}
            transition={{ duration: 0.8, delay: 0.05 }}
            className={`${card} md:col-span-7 relative min-h-[420px] flex flex-col justify-end`}
            data-testid="step-1-card"
          >
            <img
              src={QR_IMG}
              alt={s1.imgAlt}
              className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#110E0C] via-[#110E0C]/40 to-transparent" />
            <div className="relative p-8 md:p-10">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-sm bg-[#D48C46] text-[#110E0C]">
                <QrCode size={20} />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D48C46] mb-2">
                {s1.step}
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#F9F6F0] mb-3">
                {s1.title}
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-[#F9F6F0]/70 max-w-md">
                {s1.text}
              </p>
            </div>
          </motion.div>

          <motion.div
            {...reveal}
            transition={{ duration: 0.8, delay: 0.15 }}
            className={`${card} md:col-span-5 p-8 md:p-10 flex flex-col justify-between min-h-[420px]`}
            data-testid="step-2-card"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-[#D48C46]/50 text-[#D48C46]">
              <UserRoundPlus size={20} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D48C46] mb-2">
                {s2.step}
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#F9F6F0] mb-3">
                {s2.title}
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-[#A69C95]">
                {s2.text}
              </p>
            </div>
          </motion.div>

          <motion.div
            {...reveal}
            transition={{ duration: 0.8, delay: 0.1 }}
            className={`${card} md:col-span-5 p-8 md:p-10 flex flex-col justify-between min-h-[380px]`}
            data-testid="step-3-card"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-[#D48C46]/50 text-[#D48C46]">
              <MessagesSquare size={20} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D48C46] mb-2">
                {s3.step}
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#F9F6F0] mb-3">
                {s3.title}
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-[#A69C95]">
                {s3.text}
              </p>
            </div>
          </motion.div>

          <motion.div
            {...reveal}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`${card} md:col-span-7 relative min-h-[380px] flex flex-col justify-end`}
            data-testid="loyalty-card"
          >
            <img
              src={SMS_IMG}
              alt={t.how.loyalty.imgAlt}
              className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#110E0C] via-[#110E0C]/50 to-transparent" />
            <div className="relative p-8 md:p-10 flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-md">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-sm bg-[#B85D19] text-[#F9F6F0]">
                  <Flame size={20} />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#F9F6F0] mb-3">
                  {t.how.loyalty.title}
                </h3>
                <p className="text-sm md:text-base leading-relaxed text-[#F9F6F0]/70">
                  {t.how.loyalty.text}
                </p>
              </div>
              <p className="font-serif italic text-xl text-[#D48C46]">
                {t.how.loyalty.quote}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
